import React, { useState, useEffect } from 'react';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Container from '@material-ui/core/Container';
import Paper from '@material-ui/core/Paper';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../auth/AuthContext';

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.background.default,
  },
  paper: {
    padding: theme.spacing(4),
    width: '100%',
    maxWidth: 420,
  },
  logo: {
    textAlign: 'center',
    marginBottom: theme.spacing(2),
  },
  tabs: {
    marginBottom: theme.spacing(3),
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  submit: {
    marginTop: theme.spacing(1),
    height: 44,
  },
  error: {
    color: theme.palette.error.main,
    fontSize: '0.85rem',
    textAlign: 'center',
  },
}));

const LoginPage: React.FC = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isAuthenticated,
    signIn,
    signInWithBarcode,
    submitNewPassword,
    requiresPasswordChange,
    passwordExpired,
    ssprStep,
    ssprCodeSentTo,
    initiatePasswordReset,
    verifyResetCode,
    completePasswordReset,
    isLoading,
    error,
    clearError,
  } = useAuthContext();

  useEffect(() => {
    if (isAuthenticated) {
      // Return to the page that bounced us here (deep links like
      // /repair-detail/{barcode}); fall back to home.
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      const state = location.state as { from?: string } | null;
      const from = state?.from && state.from.startsWith('/') && !state.from.startsWith('/login')
        ? state.from
        : '/';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location.state]);

  const [tab, setTab] = useState<0 | 1>(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [barcode, setBarcode] = useState('');
  const [pin, setPin] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);

  // eslint-disable-next-line @typescript-eslint/ban-types
  const handleTabChange = (_: React.ChangeEvent<{}>, newValue: 0 | 1) => {
    clearError();
    setTab(newValue);
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    await signIn(email, password);
  };

  const handleBarcodeSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    await signInWithBarcode(barcode, pin);
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    await submitNewPassword(newPassword, confirmPassword);
  };

  const handleSendResetCode = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    await initiatePasswordReset(email);
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    await verifyResetCode(otpCode);
  };

  const handleCompleteReset = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    await completePasswordReset(newPassword, confirmPassword);
  };

  // ── ForceChangePasswordNextSignIn ────────────────────────────────────────────
  if (requiresPasswordChange) {
    return (
      <div className={classes.root}>
        <Container>
          <Paper className={classes.paper} elevation={3}>
            <div className={classes.logo}>
              <Typography variant="h5">{'Set New Password'}</Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                {'Your password must be changed before you can continue.'}
              </Typography>
            </div>
            <form className={classes.form} onSubmit={handlePasswordChange}>
              <TextField
                label="New Password"
                type="password"
                variant="outlined"
                fullWidth
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={isLoading}
                autoFocus
              />
              <TextField
                label="Confirm New Password"
                type="password"
                variant="outlined"
                fullWidth
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isLoading}
              />
              {error && <Typography className={classes.error}>{error}</Typography>}
              <Button
                type="submit"
                variant="contained"
                color="primary"
                className={classes.submit}
                disabled={isLoading || !newPassword || !confirmPassword}
                fullWidth
              >
                {isLoading ? <CircularProgress size={22} color="inherit" /> : 'Set Password'}
              </Button>
            </form>
          </Paper>
        </Container>
      </div>
    );
  }

  // ── Password expired — step 1 ────────────────────────────────────────────────
  if (passwordExpired && ssprStep === null) {
    return (
      <div className={classes.root}>
        <Container>
          <Paper className={classes.paper} elevation={3}>
            <div className={classes.logo}>
              <Typography variant="h5">{'Password Expired'}</Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                {'Your password has expired. We\'ll send a one-time code to your email to reset it.'}
              </Typography>
            </div>
            <form className={classes.form} onSubmit={handleSendResetCode}>
              {error && <Typography className={classes.error}>{error}</Typography>}
              <Button
                type="submit"
                variant="contained"
                color="primary"
                className={classes.submit}
                disabled={isLoading}
                fullWidth
              >
                {isLoading ? <CircularProgress size={22} color="inherit" /> : 'Send Reset Code'}
              </Button>
            </form>
          </Paper>
        </Container>
      </div>
    );
  }

  // ── Password expired — step 2: enter OTP ────────────────────────────────────
  if (ssprStep === 'otp') {
    return (
      <div className={classes.root}>
        <Container>
          <Paper className={classes.paper} elevation={3}>
            <div className={classes.logo}>
              <Typography variant="h5">{'Enter Code'}</Typography>
              {ssprCodeSentTo && (
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                  {`A code was sent to ${ssprCodeSentTo}`}
                </Typography>
              )}
            </div>
            <form className={classes.form} onSubmit={handleVerifyCode}>
              <TextField
                label="Verification Code"
                type="text"
                variant="outlined"
                fullWidth
                required
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value)}
                disabled={isLoading}
                autoFocus
                inputProps={{ maxLength: 8 }}
              />
              {error && <Typography className={classes.error}>{error}</Typography>}
              <Button
                type="submit"
                variant="contained"
                color="primary"
                className={classes.submit}
                disabled={isLoading || !otpCode}
                fullWidth
              >
                {isLoading ? <CircularProgress size={22} color="inherit" /> : 'Verify Code'}
              </Button>
            </form>
          </Paper>
        </Container>
      </div>
    );
  }

  // ── Password expired — step 3: set new password ──────────────────────────────
  if (ssprStep === 'newPassword') {
    return (
      <div className={classes.root}>
        <Container>
          <Paper className={classes.paper} elevation={3}>
            <div className={classes.logo}>
              <Typography variant="h5">{'Set New Password'}</Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                {'Choose a new password for your account.'}
              </Typography>
            </div>
            <form className={classes.form} onSubmit={handleCompleteReset}>
              <TextField
                label="New Password"
                type="password"
                variant="outlined"
                fullWidth
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={isLoading}
                autoFocus
              />
              <TextField
                label="Confirm New Password"
                type="password"
                variant="outlined"
                fullWidth
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isLoading}
              />
              {error && <Typography className={classes.error}>{error}</Typography>}
              <Button
                type="submit"
                variant="contained"
                color="primary"
                className={classes.submit}
                disabled={isLoading || !newPassword || !confirmPassword}
                fullWidth
              >
                {isLoading ? <CircularProgress size={22} color="inherit" /> : 'Reset Password'}
              </Button>
            </form>
          </Paper>
        </Container>
      </div>
    );
  }

  // ── Forgot password — step 0: enter email and request code ──────────────────
  if (forgotPasswordMode && ssprStep === null) {
    return (
      <div className={classes.root}>
        <Container>
          <Paper className={classes.paper} elevation={3}>
            <div className={classes.logo}>
              <Typography variant="h5">{'Reset Password'}</Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                {"We'll send a one-time code to your email address."}
              </Typography>
            </div>
            <form className={classes.form} onSubmit={handleSendResetCode}>
              <TextField
                label="Email"
                type="email"
                variant="outlined"
                fullWidth
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                autoFocus
                autoComplete="username"
              />
              {error && <Typography className={classes.error}>{error}</Typography>}
              <Button
                type="submit"
                variant="contained"
                color="primary"
                className={classes.submit}
                disabled={isLoading || !email}
                fullWidth
              >
                {isLoading ? <CircularProgress size={22} color="inherit" /> : 'Send Reset Code'}
              </Button>
              <Button
                variant="text"
                color="default"
                fullWidth
                disabled={isLoading}
                onClick={() => { clearError(); setForgotPasswordMode(false); }}
              >
                {'Back to login'}
              </Button>
            </form>
          </Paper>
        </Container>
      </div>
    );
  }

  // ── Normal login (tabbed) ────────────────────────────────────────────────────
  return (
    <div className={classes.root}>
      <Container>
        <Paper className={classes.paper} elevation={3}>
          <div className={classes.logo}>
            <Typography variant="h4" style={{ fontWeight: 600 }}>{'JEMS'}</Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
              {'Sign in to continue'}
            </Typography>
          </div>

          <Tabs
            value={tab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            className={classes.tabs}
          >
            <Tab label="Email / Password" />
            <Tab label="Barcode / PIN" />
          </Tabs>

          {tab === 0 && (
            <form className={classes.form} onSubmit={handleSignIn}>
              <TextField
                label="Email"
                type="email"
                variant="outlined"
                fullWidth
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                autoFocus
                autoComplete="username"
              />
              <TextField
                label="Password"
                type="password"
                variant="outlined"
                fullWidth
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                autoComplete="current-password"
              />
              {error && <Typography className={classes.error}>{error}</Typography>}
              <Button
                type="submit"
                variant="contained"
                color="primary"
                className={classes.submit}
                disabled={isLoading || !email || !password}
                fullWidth
              >
                {isLoading ? <CircularProgress size={22} color="inherit" /> : 'Sign In'}
              </Button>
              <Button
                type="button"
                variant="text"
                color="default"
                fullWidth
                disabled={isLoading}
                onClick={() => { clearError(); setForgotPasswordMode(true); }}
              >
                {'Forgot password?'}
              </Button>
            </form>
          )}

          {tab === 1 && (
            <form className={classes.form} onSubmit={handleBarcodeSignIn}>
              <TextField
                label="Barcode"
                type="text"
                variant="outlined"
                fullWidth
                required
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
                disabled={isLoading}
                autoFocus
                autoComplete="off"
                inputProps={{ maxLength: 64 }}
              />
              <TextField
                label="PIN"
                type="password"
                variant="outlined"
                fullWidth
                required
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                disabled={isLoading}
                autoComplete="off"
                inputProps={{ maxLength: 16 }}
              />
              {error && <Typography className={classes.error}>{error}</Typography>}
              <Button
                type="submit"
                variant="contained"
                color="primary"
                className={classes.submit}
                disabled={isLoading || !barcode || !pin}
                fullWidth
              >
                {isLoading ? <CircularProgress size={22} color="inherit" /> : 'Sign In'}
              </Button>
            </form>
          )}
        </Paper>
      </Container>
    </div>
  );
};

export default LoginPage;
